/**
 * Block parser for the docs Markdown dialect (DOCS_CONTRACT.md):
 * CommonMark basics + GFM tables and fenced code, `:::note|tip|warning`
 * callouts, and figures written as a paragraph that is only
 * `![caption](shot:<id>)` or `![caption](video:<id>)`.
 *
 * It produces plain objects; `DocMarkdown` renders them as React elements, so
 * no HTML string is ever injected (the dialect allows no raw HTML beyond
 * <kbd>, which the inline renderer handles as a token).
 */

const FIGURE = /^!\[([^\]]*)\]\((shot|video):([a-z0-9-]+)\)$/i;
const CALLOUT_OPEN = /^:::(note|tip|warning)\s*$/i;
const CALLOUT_CLOSE = /^:::\s*$/;
const HEADING = /^(#{1,6})\s+(.*?)\s*#*\s*$/;
const HR = /^\s*([-*_])(\s*\1){2,}\s*$/;
const UL = /^\s*[-*+]\s+(.*)$/;
const OL = /^\s*\d+[.)]\s+(.*)$/;
const QUOTE = /^>\s?(.*)$/;
const FENCE = /^```\s*([\w-]*)\s*$/;

const isTableSep = (s) => s.includes('-') && /^\s*\|?\s*:?-+:?\s*(\|\s*:?-+:?\s*)*\|?\s*$/.test(s);
const splitRow = (row) =>
  row
    .trim()
    .replace(/^\|/, '')
    .replace(/\|$/, '')
    .split('|')
    .map((c) => c.trim());

function startsBlock(line, next) {
  return (
    /^\s*$/.test(line) ||
    FENCE.test(line) ||
    HEADING.test(line) ||
    CALLOUT_OPEN.test(line) ||
    CALLOUT_CLOSE.test(line) ||
    UL.test(line) ||
    OL.test(line) ||
    QUOTE.test(line) ||
    FIGURE.test(line.trim()) ||
    (line.includes('|') && next !== undefined && isTableSep(next))
  );
}

export function parseDocMarkdown(md) {
  const lines = String(md || '').replace(/\r\n?/g, '\n').split('\n');
  return parseLines(lines);
}

function parseLines(lines) {
  const blocks = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];

    if (/^\s*$/.test(line)) {
      i += 1;
      continue;
    }

    const fence = line.match(FENCE);
    if (fence) {
      const code = [];
      i += 1;
      while (i < lines.length && !/^```\s*$/.test(lines[i])) code.push(lines[i++]);
      i += 1;
      blocks.push({ type: 'code', lang: fence[1] || '', code: code.join('\n') });
      continue;
    }

    const callout = line.match(CALLOUT_OPEN);
    if (callout) {
      const inner = [];
      i += 1;
      while (i < lines.length && !CALLOUT_CLOSE.test(lines[i])) inner.push(lines[i++]);
      i += 1;
      blocks.push({ type: 'callout', kind: callout[1].toLowerCase(), blocks: parseLines(inner) });
      continue;
    }

    const heading = line.match(HEADING);
    if (heading) {
      blocks.push({ type: 'heading', level: heading[1].length, text: heading[2] });
      i += 1;
      continue;
    }

    if (HR.test(line)) {
      blocks.push({ type: 'hr' });
      i += 1;
      continue;
    }

    const figure = line.trim().match(FIGURE);
    if (figure) {
      blocks.push({ type: 'figure', media: figure[2].toLowerCase(), id: figure[3], caption: figure[1] });
      i += 1;
      continue;
    }

    if (line.includes('|') && i + 1 < lines.length && isTableSep(lines[i + 1])) {
      const head = splitRow(line);
      i += 2;
      const rows = [];
      while (i < lines.length && lines[i].includes('|') && !/^\s*$/.test(lines[i])) rows.push(splitRow(lines[i++]));
      blocks.push({ type: 'table', head, rows });
      continue;
    }

    if (UL.test(line) || OL.test(line)) {
      const ordered = !UL.test(line);
      const re = ordered ? OL : UL;
      const items = [];
      while (i < lines.length && re.test(lines[i])) {
        let item = lines[i++].match(re)[1];
        // A hard-wrapped item continues on indented lines ("1. Type …\n   in red").
        while (i < lines.length && /^\s+\S/.test(lines[i]) && !UL.test(lines[i]) && !OL.test(lines[i])) {
          item += ` ${lines[i++].trim()}`;
        }
        items.push(item);
        // A "loose" list (blank line between items) is still one list.
        if (/^\s*$/.test(lines[i] ?? 'x') && i + 1 < lines.length && re.test(lines[i + 1])) i += 1;
      }
      blocks.push({ type: 'list', ordered, items });
      continue;
    }

    if (QUOTE.test(line)) {
      const inner = [];
      while (i < lines.length && QUOTE.test(lines[i])) inner.push(lines[i++].match(QUOTE)[1]);
      blocks.push({ type: 'quote', text: inner.join(' ') });
      continue;
    }

    const para = [line];
    i += 1;
    while (i < lines.length && !startsBlock(lines[i], lines[i + 1])) para.push(lines[i++]);
    blocks.push({ type: 'paragraph', text: para.join(' ') });
  }
  return blocks;
}
